/**
 * AdminUniversityTranscripts — Per-student transcript builder + downloads.
 *
 *   • Search/select student
 *   • Live JSON preview of transcript rows
 *   • Download buttons for branded PDF and CSV
 *   • "Email Transcript to Student" action
 */
import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import {
  Loader2, Search, FileText, Download, Mail, User, GraduationCap, CheckCircle2, XCircle,
} from "lucide-react";

export default function AdminUniversityTranscripts() {
  const [q, setQ] = useState("");
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const adminEmail = adminAuth.getEmail?.() || "admin@kcis.com";

  const search = async (term) => {
    try {
      const r = await universityApi.gradingStudents({ q: term, limit: 30 });
      setStudents(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e, "Search failed")); }
  };
  useEffect(() => { search(""); }, []);

  useEffect(() => {
    if (!selected) { setTranscript(null); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await universityApi.transcriptJson(selected.student_id);
        setTranscript(r.data);
      } catch (e) { toast.error(formatApiError(e, "Transcript failed")); }
      finally { setLoading(false); }
    })();
  }, [selected]);

  const tokenizedUrl = (url) => {
    // Append token as query so the browser GET works with admin auth header.
    // Backend require_admin accepts Authorization header — we route via fetch+blob.
    return url;
  };

  const downloadBlob = async (url, filename) => {
    try {
      const token = adminAuth.getToken();
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error(`Download failed: ${e.message}`); }
  };

  const emailTranscript = async () => {
    if (!selected) return;
    if (!confirm(`Email transcript to ${selected.student_email}?`)) return;
    setEmailing(true);
    try {
      const r = await universityApi.transcriptEmail(selected.student_id, adminEmail);
      if (r.data?.emailed) toast.success(`Transcript emailed to ${selected.student_email}`);
      else toast.warning("Transcript generated but email queue unavailable (Resend domain unverified)");
    } catch (e) { toast.error(formatApiError(e, "Email failed")); }
    finally { setEmailing(false); }
  };

  return (
    <AdminShell door="university" title="Student Transcripts">
      <div data-testid="admin-transcripts-page" className="space-y-6">
        {/* Search */}
        <section className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              className="form-input"
              placeholder="Search by name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search(q)}
              data-testid="transcripts-search-input"
            />
            <button
              onClick={() => search(q)}
              className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm h-10 px-4"
              data-testid="transcripts-search-btn"
            >Search</button>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
            {students.map((s) => (
              <button
                key={s.student_id}
                onClick={() => setSelected(s)}
                className={`text-left rounded-xl border px-3 py-2 transition ${
                  selected?.student_id === s.student_id
                    ? "border-purple-900 bg-purple-50"
                    : "border-slate-200 hover:border-purple-300"
                }`}
                data-testid={`transcripts-student-${s.student_id}`}
              >
                <div className="text-sm font-medium text-slate-900 truncate">{s.student_name}</div>
                <div className="text-xs text-slate-500 truncate">{s.student_email}</div>
              </button>
            ))}
          </div>
        </section>

        {!selected ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            Select a student above to view their transcript.
          </div>
        ) : loading ? (
          <div className="text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : !transcript ? null : (
          <>
            {/* Header */}
            <section className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="inline-flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-900 text-white inline-flex items-center justify-center">
                    <GraduationCap className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-purple-900">KCIS / KISSED University · Official Transcript</div>
                    <div className="font-display text-xl text-slate-900" data-testid="transcripts-name">{transcript.student_name}</div>
                    <div className="text-xs text-slate-500">{transcript.student_id}</div>
                    {transcript.primary_program && (
                      <div className="text-xs text-slate-600 mt-0.5">Program: <b>{transcript.primary_program}</b></div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => downloadBlob(universityApi.transcriptPdfUrl(selected.student_id), `kcis_transcript_${selected.student_id}.pdf`)}
                    className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-2"
                    data-testid="transcripts-download-pdf"
                  >
                    <Download className="w-4 h-4" />Download PDF
                  </button>
                  <button
                    onClick={() => downloadBlob(universityApi.transcriptCsvUrl(selected.student_id), `kcis_transcript_${selected.student_id}.csv`)}
                    className="rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm h-9 px-4 inline-flex items-center gap-2"
                    data-testid="transcripts-download-csv"
                  >
                    <FileText className="w-4 h-4" />Download CSV
                  </button>
                  <button
                    onClick={emailTranscript}
                    disabled={emailing}
                    className="rounded-xl border border-emerald-300 hover:bg-emerald-50 text-emerald-800 text-sm h-9 px-4 inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="transcripts-email-btn"
                  >
                    {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Email Student
                  </button>
                </div>
              </div>
            </section>

            {/* Table */}
            <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-purple-900 text-white">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Course</th>
                    <th className="text-left px-4 py-2 font-semibold">Title</th>
                    <th className="text-left px-4 py-2 font-semibold">Level / Credential</th>
                    <th className="text-right px-4 py-2 font-semibold">Clock</th>
                    <th className="text-right px-4 py-2 font-semibold">CE</th>
                    <th className="text-right px-4 py-2 font-semibold">Score</th>
                    <th className="text-center px-4 py-2 font-semibold">Result</th>
                    <th className="text-left px-4 py-2 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transcript.rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-500" data-testid="transcripts-empty">
                        No completed coursework yet for this student.
                      </td>
                    </tr>
                  ) : transcript.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`transcripts-row-${i}`}>
                      <td className="px-4 py-2 font-mono text-purple-900">{r.course_code}</td>
                      <td className="px-4 py-2">{r.course_title}</td>
                      <td className="px-4 py-2 text-slate-600">{r.credential || "—"}</td>
                      <td className="px-4 py-2 text-right">{r.clock_hours || "—"}</td>
                      <td className="px-4 py-2 text-right">{r.ce_hours || "—"}</td>
                      <td className="px-4 py-2 text-right">{r.score != null ? `${r.score}/${r.max_score}` : "—"}</td>
                      <td className="px-4 py-2 text-center">
                        {r.passed
                          ? <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 className="w-3.5 h-3.5" />PASS</span>
                          : <span className="inline-flex items-center gap-1 text-rose-700 font-medium"><XCircle className="w-3.5 h-3.5" />FAIL</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{r.completion_date ? new Date(r.completion_date).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* Cumulative */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-xs uppercase tracking-wide text-purple-900 mb-3">Cumulative Summary</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Cumulative Clock Hours" value={transcript.cumulative.clock_hours.toFixed(1)} />
                <Stat label="Cumulative CE Hours"    value={transcript.cumulative.ce_hours.toFixed(1)} />
                <Stat label="Courses Completed"      value={transcript.cumulative.completed_courses} tone="green" />
                <Stat label="Total Attempts"         value={transcript.cumulative.total_attempts} tone="slate" />
              </div>
              <div className="mt-4 text-[11px] text-slate-500">
                Generated {new Date(transcript.generated_at).toLocaleString()}
              </div>
            </section>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700",
    green: "bg-emerald-50 text-emerald-800",
    purple: "bg-purple-50 text-purple-900",
  };
  return (
    <div className={`rounded-xl ${tones[tone]} px-4 py-3`}>
      <div className="text-[11px] uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}
